import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { useNavigate, Navigate, Link } from 'react-router-dom'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent } from "@/components/ui/card"
import { EyeIcon, EyeOffIcon, LockIcon, MailIcon, Shield, KeyRound, ArrowLeft } from 'lucide-react'
import { useAuth } from "@/context/AuthContext"
import { AuthenticationCheck } from "@/components/auth/AuthenticationCheck"
import { useEnhancedToast } from "@/components/ui/enhanced-toast"
import { ShieldCheck, ShieldX } from "lucide-react"
import { useTenant } from "@/context/TenantContext"
import { useTheme } from "@/context/ThemeContext"
import { login, verify2FALogin } from "@/api"
import type { LoginResponse } from "@/api/auth/auth.types"
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSlot,
    InputOTPSeparator,
} from "@/components/ui/input-otp"
import { REGEXP_ONLY_DIGITS_AND_CHARS } from "input-otp"

export default function Login() {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    
    // 2FA state
    const [requires2FA, setRequires2FA] = useState(false)
    const [tempToken, setTempToken] = useState('')
    const [twoFACode, setTwoFACode] = useState('')
    const [isBackupCode, setIsBackupCode] = useState(false)
    const [isVerifying2FA, setIsVerifying2FA] = useState(false)
    
    const navigate = useNavigate()
    const { error, success } = useEnhancedToast()
    const { setAuthData, isAuthenticated, isLoading: authLoading } = useAuth()
    const { branding } = useTenant();
    const { theme } = useTheme();

    // Determine if we should use dark logo
    const isDarkMode = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const effectiveLogoUrl = isDarkMode && branding?.dark_logo_url ? branding.dark_logo_url : branding?.logo_url;

    // Show loading state while checking authentication
    if (authLoading) {
        return <AuthenticationCheck />;
    }

    // Redirect authenticated users
    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)
    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => setPassword(event.target.value)

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsLoading(true);

        try {
            const result = await login({ email, password });

            if (result.error) {
                error({
                    icon: ShieldX,
                    title: "Authentication Failed",
                    description: result.error || "Please verify your credentials and try again.",
                    duration: 5000,
                });
            } else if (result.data) {
                const loginResponse = result.data as LoginResponse;
                
                // Check if 2FA is required
                if (loginResponse.requires_2fa && loginResponse.temp_token) {
                    setTempToken(loginResponse.temp_token);
                    setRequires2FA(true);
                } else {
                    // Normal login - tokens already stored by API layer
                    await setAuthData();
                    success({
                        icon: ShieldCheck,
                        title: "Authentication Successful",
                        description: "You have successfully logged in.",
                        duration: 4000,
                    });
                }
            }
        } catch (err: unknown) {
            error({
                icon: ShieldX,
                title: "Authentication Failed",
                description: err instanceof Error ? err.message : "An unexpected error occurred",
                duration: 5000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handle2FASubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        
        if (!twoFACode) {
            error({
                icon: ShieldX,
                title: "Code Required",
                description: "Please enter your verification code",
                duration: 4000,
            });
            return;
        }

        setIsVerifying2FA(true);

        try {
            const result = await verify2FALogin({
                temp_token: tempToken,
                code: twoFACode,
                is_backup_code: isBackupCode,
            });

            if (result.error) {
                error({
                    icon: ShieldX,
                    title: "Verification Failed",
                    description: result.error || "Invalid verification code. Please try again.",
                    duration: 5000,
                });
            } else if (result.data) {
                // Tokens stored by API layer, fetch user data
                await setAuthData();
                success({
                    icon: ShieldCheck,
                    title: "Authentication Successful",
                    description: "You have successfully logged in.",
                    duration: 4000,
                });
            }
        } catch (err: unknown) {
            error({
                icon: ShieldX,
                title: "Verification Failed",
                description: err instanceof Error ? err.message : "An unexpected error occurred",
                duration: 5000,
            });
        } finally {
            setIsVerifying2FA(false);
        }
    };

    const handleBack = () => {
        setRequires2FA(false);
        setTempToken('');
        setTwoFACode('');
        setIsBackupCode(false);
    };

    const containerVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.2,
                ease: "easeOut"
            }
        },
        exit: {
            opacity: 0,
            y: -10,
            transition: {
                duration: 0.2,
                ease: "easeIn"
            }
        }
    }

    const renderLogo = () => {
        if (!effectiveLogoUrl) {
            return (
                <span className="text-2xl font-semibold text-primary">
                    {branding?.company_name || 'VoiceBot'}
                </span>
            );
        }

        return (
            <img
                src={effectiveLogoUrl}
                alt={branding?.company_name || 'Company Logo'}
                className="max-h-12 sm:max-h-14 w-auto max-w-[180px] sm:max-w-[220px] object-contain"
                onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    const parent = target.parentElement;
                    if (parent) {
                        parent.innerHTML = `<span class="text-2xl font-semibold text-primary">${branding?.company_name || 'VoiceBot'}</span>`;
                    }
                }}
            />
        );
    };

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="w-full max-w-md mx-auto"
        >
            <Card className="shadow-lg border">
                <CardContent className="p-6 sm:p-8 space-y-6">
                    {/* Logo */}
                    <div className="flex flex-col items-center space-y-6">
                        <div className="flex items-center justify-center min-h-12 max-h-16 py-2">
                            {renderLogo()}
                        </div>
                        <AnimatePresence mode="wait">
                            {requires2FA ? (
                                <motion.div
                                    key="2fa-header"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="text-center space-y-1.5"
                                >
                                    <div className="flex items-center justify-center gap-2 mb-2">
                                        <div className="p-2 rounded-full bg-primary/10">
                                            <Shield className="h-5 w-5 text-primary" />
                                        </div>
                                    </div>
                                    <h1 className="text-xl font-semibold tracking-tight">Two-Factor Authentication</h1>
                                    <p className="text-sm text-muted-foreground/80">
                                        Enter the code from your authenticator app
                                    </p>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="login-header"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="text-center space-y-1.5"
                                >
                                    <h1 className="text-xl font-semibold tracking-tight">Welcome back</h1>
                                    <p className="text-sm text-muted-foreground/80">
                                        Sign in to your account to continue
                                    </p>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    <AnimatePresence mode="wait">
                        {requires2FA ? (
                            /* 2FA Verification Form */
                            <motion.form
                                key="2fa-form"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.2 }}
                                onSubmit={handle2FASubmit}
                                className="space-y-4"
                            >
                                {/* Code Input */}
                                <div className="space-y-3">
                                    <Label className="text-sm font-medium text-center block">
                                        {isBackupCode ? 'Backup Code' : 'Verification Code'}
                                    </Label>
                                    
                                    {isBackupCode ? (
                                        /* Backup Code Input - alphanumeric */
                                        <div className="flex justify-center">
                                            <InputOTP
                                                maxLength={8}
                                                value={twoFACode}
                                                onChange={(value) => setTwoFACode(value.toUpperCase())}
                                                pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
                                                autoFocus
                                            >
                                                <InputOTPGroup>
                                                    <InputOTPSlot index={0} />
                                                    <InputOTPSlot index={1} />
                                                    <InputOTPSlot index={2} />
                                                    <InputOTPSlot index={3} />
                                                </InputOTPGroup>
                                                <InputOTPSeparator />
                                                <InputOTPGroup>
                                                    <InputOTPSlot index={4} />
                                                    <InputOTPSlot index={5} />
                                                    <InputOTPSlot index={6} />
                                                    <InputOTPSlot index={7} />
                                                </InputOTPGroup>
                                            </InputOTP>
                                        </div>
                                    ) : (
                                        /* TOTP Code Input - numeric only */
                                        <div className="flex justify-center">
                                            <InputOTP
                                                maxLength={6}
                                                value={twoFACode}
                                                onChange={(value) => setTwoFACode(value)}
                                                autoFocus
                                            >
                                                <InputOTPGroup>
                                                    <InputOTPSlot index={0} />
                                                    <InputOTPSlot index={1} />
                                                    <InputOTPSlot index={2} />
                                                </InputOTPGroup>
                                                <InputOTPSeparator />
                                                <InputOTPGroup>
                                                    <InputOTPSlot index={3} />
                                                    <InputOTPSlot index={4} />
                                                    <InputOTPSlot index={5} />
                                                </InputOTPGroup>
                                            </InputOTP>
                                        </div>
                                    )}
                                </div>

                                {/* Toggle backup code mode */}
                                <div className="flex justify-center">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsBackupCode(!isBackupCode);
                                            setTwoFACode('');
                                        }}
                                        className="text-sm text-primary hover:text-primary/80 transition-colors font-medium"
                                    >
                                        {isBackupCode ? 'Use authenticator code instead' : 'Use a backup code instead'}
                                    </button>
                                </div>

                                {/* Submit Button */}
                                <Button
                                    type="submit"
                                    className="w-full h-9"
                                    disabled={isVerifying2FA || (isBackupCode ? twoFACode.length !== 8 : twoFACode.length !== 6)}
                                >
                                    {isVerifying2FA ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Verifying...
                                        </span>
                                    ) : (
                                        'Verify'
                                    )}
                                </Button>

                                {/* Back Button */}
                                <div className="text-center">
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        <ArrowLeft className="h-3.5 w-3.5" />
                                        Back to login
                                    </button>
                                </div>
                            </motion.form>
                        ) : (
                            /* Login Form */
                            <motion.form
                                key="login-form"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.2 }}
                                onSubmit={handleSubmit}
                                className="space-y-4"
                            >
                                {/* Email */}
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-sm font-medium">
                                        Email
                                    </Label>
                                    <div className="relative">
                                        <MailIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="email"
                                            value={email}
                                            onChange={handleEmailChange}
                                            placeholder="Enter your email"
                                            type="email"
                                            autoComplete="email"
                                            required
                                            className="pl-9 h-9"
                                        />
                                    </div>
                                </div>

                                {/* Password */}
                                <div className="space-y-2">
                                    <Label htmlFor="password" className="text-sm font-medium">
                                        Password
                                    </Label>
                                    <div className="relative">
                                        <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="password"
                                            value={password}
                                            onChange={handlePasswordChange}
                                            type={showPassword ? "text" : "password"}
                                            placeholder="Enter your password"
                                            autoComplete="current-password"
                                            className="pl-9 pr-9 h-9"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                        >
                                            {showPassword ? (
                                                <EyeOffIcon className="h-4 w-4" />
                                            ) : (
                                                <EyeIcon className="h-4 w-4" />
                                            )}
                                        </button>
                                    </div>
                                    <div className="flex justify-end">
                                        <Link
                                            to="/auth/forgot-password"
                                            className="text-sm text-primary hover:text-primary/80 transition-colors font-medium"
                                        >
                                            Forgot password?
                                        </Link>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <Button
                                    type="submit"
                                    className="w-full h-9"
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Signing in...
                                        </span>
                                    ) : (
                                        'Sign in'
                                    )}
                                </Button>
                            </motion.form>
                        )}
                    </AnimatePresence>

                    {/* Sign Up Link - only show on login form */}
                    {!requires2FA && (
                        <div className="text-center text-sm text-muted-foreground/80">
                            Don't have an account?{" "}
                            <button
                                onClick={() => navigate("/auth/signup")}
                                className="text-primary hover:text-primary/80 transition-colors font-medium"
                            >
                                Sign up
                            </button>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Footer Links */}
            <div className="mt-6 flex flex-col items-center gap-4">
                <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground/70">
                    <Link 
                        to="/terms-of-service" 
                        className="hover:text-foreground/80 transition-colors"
                    >
                        Terms of Service
                    </Link>
                    <span>•</span>
                    <Link 
                        to="/privacy-policy" 
                        className="hover:text-foreground/80 transition-colors"
                    >
                        Privacy Policy
                    </Link>
                </div>
                <div className="flex justify-center">
                    <div className="translate-x-8">
                        <iframe
                            src="https://status.voicebot.studio/badge?theme=light"
                            width="240"
                            height="30"
                            className="border-0 dark:hidden"
                            title="Service Status"
                        />
                        <iframe
                            src="https://status.voicebot.studio/badge?theme=dark"
                            width="240"
                            height="30"
                            className="border-0 hidden dark:block"
                            title="Service Status"
                        />
                    </div>
                </div>
            </div>
        </motion.div>
    )
}
